import React, { FC, useMemo } from "react";

import { useTransactionDialogs } from "../hooks/useTransactionDialogs";
import { useTransactionHandlers } from "../hooks/useTransactionHandlers";
import { CashECTransactionTable } from "../components/CashECTransactionTable";
import { BankTransferTable } from "../components/BankTransferTable";
import { RechnungTable } from "../components/RechnungTable";
import { TransactionDialogs } from "../components/TransactionDialogs";
import LastschriftTransactionTable from "../components/LastschriftTransactionTable";
import { useUserId } from "../../../../../stores/User/User.hooks";
import { AllTransactionMethods } from "../types/kasse.types";
import {
  BaseTransactionDetailsDto,
  GetBankTransferDto,
  GetRechnungDetailsDto,
} from "../../../../../api-client";
import { LastSchriftDetailsDto } from "../types/sepa-export.types";

interface TransactionsOverviewTableProps {
  transactionMethod: AllTransactionMethods;
  transactions: any[];
  onRefresh?: () => void;
  accountId?: string;
}

/**
 * REFACTORED TransactionTable (v2)
 *
 * Improvements over original:
 * - Reduced from 831 lines to ~150 lines
 * - Follows Single Responsibility Principle
 * - Extracted custom hooks for state and handlers
 * - Separated table components by transaction type
 * - Consolidated dialogs in one component
 * - Better testability and maintainability
 * - Easier to extend with new transaction types
 */
const TransactionsOverviewTable: FC<TransactionsOverviewTableProps> = ({
  transactionMethod,
  transactions,
  onRefresh,
  accountId,
}) => {
  const userId = useUserId();

  // Custom hooks for state management
  const dialogs = useTransactionDialogs();
  const handlers = useTransactionHandlers({ onRefresh });

  // ==================== CASH HANDLERS ====================
  const handleOpenCancelCashDialog = (transaction: BaseTransactionDetailsDto) => {
    dialogs.setTransactionToDelete(transaction);
    dialogs.setCancelCashDialogOpen(true);
  };

  const handleCancelCashConfirm = async (reason: string) => {
    if (!dialogs.transactionToDelete) return;
    await handlers.handleCashCancellation(dialogs.transactionToDelete, reason);
    dialogs.setCancelCashDialogOpen(false);
    dialogs.setTransactionToDelete(null);
  };

  // ==================== EC HANDLERS ====================
  const handleOpenCancelECDialog = (transaction: BaseTransactionDetailsDto) => {
    dialogs.setTransactionToDelete(transaction);
    dialogs.setCancelECDialogOpen(true);
  };

  const handleCancelECConfirm = async (reason: string) => {
    if (!dialogs.transactionToDelete) return;
    await handlers.handleECCancellation(dialogs.transactionToDelete, reason);
    dialogs.setCancelECDialogOpen(false);
    dialogs.setTransactionToDelete(null);
  };

  // ==================== BANK TRANSFER HANDLERS ====================
  const handleOpenCancelBankTransferDialog = (transaction: GetBankTransferDto) => {
    dialogs.setTransactionToDelete(transaction);
    dialogs.setCancelBankTransferDialogOpen(true);
  };

  const handleCancelBankTransferConfirm = async (reason: string) => {
    if (!dialogs.transactionToDelete) return;
    await handlers.handleBankTransferCancellation(dialogs.transactionToDelete, reason);
    dialogs.setCancelBankTransferDialogOpen(false);
    dialogs.setTransactionToDelete(null);
  };

  // ==================== RECHNUNG HANDLERS ====================
  const handleOpenPayDialog = (rechnung: GetRechnungDetailsDto) => {
    dialogs.setSelectedRechnung(rechnung);
    dialogs.setPayDialogOpen(true);
  };

  const handleOpenDeleteDialog = (rechnung: GetRechnungDetailsDto) => {
    dialogs.setDeleteRechnung(rechnung);
    dialogs.setDeleteDialogOpen(true);
  };

  const handleDeleteRechnungConfirm = async () => {
    if (!dialogs.deleteRechnung) return;
    await handlers.handleRechnungDeletion(dialogs.deleteRechnung);
    dialogs.setDeleteDialogOpen(false);
    dialogs.setDeleteRechnung(null);
  };

  const handlePaymentSuccess = () => {
    dialogs.setPayDialogOpen(false);
    onRefresh?.();
  };

  // ==================== RENDER APPROPRIATE TABLE ====================
  const renderTable = useMemo(() => {
    switch (transactionMethod) {
      case "CASH":
        return (
          <CashECTransactionTable
            transactions={transactions as BaseTransactionDetailsDto[]}
            transactionType="CASH"
            onCancel={handleOpenCancelCashDialog}
          />
        );

      case "EC":
        return (
          <CashECTransactionTable
            transactions={transactions as BaseTransactionDetailsDto[]}
            transactionType="EC"
            onCancel={handleOpenCancelECDialog}
          />
        );

      case "BANKTRANSFER":
        return (
          <BankTransferTable
            transactions={transactions as GetBankTransferDto[]}
            onCancel={handleOpenCancelBankTransferDialog}
          />
        );

      case "RECHNUNG":
        return (
          <RechnungTable
            transactions={transactions as GetRechnungDetailsDto[]}
            onPay={handleOpenPayDialog}
            onDelete={handleOpenDeleteDialog}
            onCreateReminder={handlers.handleCreateReminder}
          />
        );

      case "LASTSCHRIFT":
        return (
          <LastschriftTransactionTable
            transactions={transactions as LastSchriftDetailsDto[]}
            onRefresh={onRefresh}
            accountId={accountId}
          />
        );

      default:
        return null;
    }
  }, [transactionMethod, transactions]);

  return (
    <>
      {renderTable}

      {/* Consolidated Dialogs */}
      <TransactionDialogs
        // States
        cancelCashDialogOpen={dialogs.cancelCashDialogOpen}
        cancelECDialogOpen={dialogs.cancelECDialogOpen}
        cancelBankTransferDialogOpen={dialogs.cancelBankTransferDialogOpen}
        deleteDialogOpen={dialogs.deleteDialogOpen}
        payDialogOpen={dialogs.payDialogOpen}
        // Data
        selectedRechnung={dialogs.selectedRechnung}
        deleteRechnung={dialogs.deleteRechnung}
        userId={userId ?? null}
        // Handlers
        onCancelCash={handleCancelCashConfirm}
        onCancelEC={handleCancelECConfirm}
        onCancelBankTransfer={handleCancelBankTransferConfirm}
        onDeleteRechnung={handleDeleteRechnungConfirm}
        onPaymentSuccess={handlePaymentSuccess}
        // Close handlers
        onCloseCancelCash={() => {
          dialogs.setCancelCashDialogOpen(false);
          dialogs.setTransactionToDelete(null);
        }}
        onCloseCancelEC={() => {
          dialogs.setCancelECDialogOpen(false);
          dialogs.setTransactionToDelete(null);
        }}
        onCloseCancelBankTransfer={() => {
          dialogs.setCancelBankTransferDialogOpen(false);
          dialogs.setTransactionToDelete(null);
        }}
        onCloseDeleteRechnung={() => {
          dialogs.setDeleteDialogOpen(false);
          dialogs.setDeleteRechnung(null);
        }}
        onClosePayment={() => dialogs.setPayDialogOpen(false)}
      />
    </>
  );
};

export default TransactionsOverviewTable;
